import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassIcon,
  Sync as SyncIcon,
  LinkOff as LinkOffIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../../hooks/useAuth';

interface WedmsStatusChipProps {
  status?: string | null;
  docId?: string | null;
  error?: string | null;
  size?: 'small' | 'medium';
  variant?: 'filled' | 'outlined';
}

/**
 * WeDMS 연동 상태를 표시하는 공통 Chip 컴포넌트
 * - 사용자가 WeDMS 연동하지 않은 경우: "연동 대기중" + 프로필 안내 툴팁
 * - pending: 처리 대기중
 * - processing: 처리중 (스피너)
 * - completed: 연동됨 (녹색)
 * - failed: 실패 (에러 메시지 툴팁)
 * - 그 외: 미연동
 */
const WedmsStatusChip: React.FC<WedmsStatusChipProps> = ({
  status,
  docId,
  error,
  size = 'small',
  variant = 'outlined',
}) => {
  const { user } = useAuthStore();
  const hasWedmsToken = user?.has_wedms_token;

  // 사용자가 WeDMS 연동하지 않은 경우
  if (!hasWedmsToken) {
    return (
      <Tooltip
        title="사용자 프로필에서 WeDMS 연동이 필요합니다. 프로필 페이지에서 WeDMS 계정을 연동해주세요."
        arrow
        placement="top"
      >
        <Chip
          icon={<LinkOffIcon fontSize="small" />}
          label="연동 대기중"
          size={size}
          color="warning"
          variant={variant}
          sx={{ cursor: 'help' }}
        />
      </Tooltip>
    );
  }

  // 연동 완료
  if (status === 'completed' || docId) {
    return (
      <Chip
        icon={<CheckCircleIcon fontSize="small" />}
        label="연동됨"
        size={size}
        color="success"
        variant={variant}
      />
    );
  }

  // 처리중
  if (status === 'processing') {
    return (
      <Chip
        icon={
          <SyncIcon
            fontSize="small"
            sx={{
              animation: 'spin 1s linear infinite',
              '@keyframes spin': {
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          />
        }
        label="처리중"
        size={size}
        color="info"
        variant={variant}
      />
    );
  }

  // 대기중
  if (status === 'pending') {
    return (
      <Chip
        icon={<HourglassIcon fontSize="small" />}
        label="대기중"
        size={size}
        color="info"
        variant={variant}
      />
    );
  }

  // 실패
  if (status === 'failed') {
    return (
      <Tooltip title={error || 'WeDMS 연동에 실패했습니다.'} arrow placement="top">
        <Chip
          icon={<ErrorIcon fontSize="small" />}
          label="실패"
          size={size}
          color="error"
          variant={variant}
          sx={{ cursor: 'help' }}
        />
      </Tooltip>
    );
  }

  // 미연동 (상태 없음)
  return (
    <Chip
      label="미연동"
      size={size}
      variant={variant}
    />
  );
};

export default WedmsStatusChip;
